import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, Save, RefreshCw, Info, CheckCircle, AlertCircle } from 'lucide-react';
import { RiskScoreCalculator, LikelihoodFactors, ImpactFactors } from '../../services/RiskScoreCalculator';
import { riskScoringAPI, SaveRiskScoringRequest } from '../../services/riskScoringAPI';
import { LikelihoodAssessment } from './LikelihoodAssessment';
import { ImpactAssessment } from './ImpactAssessment';

interface RiskScoringModalProps {
  isOpen: boolean;
  onClose: () => void;
  riskId: string;
  projectId: number;
  processStepName: string;
  currentLikelihood?: number;
  currentImpact?: number;
  controlEffectiveness?: number;
  onSaveComplete?: () => void; // Callback to refresh parent data
}

export const RiskScoringModal: React.FC<RiskScoringModalProps> = ({
  isOpen,
  onClose,
  riskId,
  projectId,
  processStepName,
  currentLikelihood,
  currentImpact,
  controlEffectiveness,
  onSaveComplete
}) => {
  // State for input data
  const [likelihoodData, setLikelihoodData] = useState<LikelihoodFactors>({
    volumePeriod: 'monthly',
    transactionCount: 0,
    dollarValue: 0,
    reachScale: 0,
    automationLevel: 'semi-automated',
    historicalIncidents: {
      count: 0,
      timeframe: '12 months'
    },
    processComplexity: 3,
    controlEffectiveness: controlEffectiveness || 3
  });

  const [impactData, setImpactData] = useState<ImpactFactors>({
    financial: 3,
    reputational: 3,
    regulatoryCompliance: 3,
    operational: 3,
    strategic: 3
  });

  // State for calculated scores
  const [calculatedScores, setCalculatedScores] = useState({
    likelihood: 0,
    impact: 0,
    confidence: 0,
    reasoning: '',
    impactReasoning: '',
    finalRiskScore: 0
  });

  // UI State
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingAndClosing, setIsSavingAndClosing] = useState(false); // Separate state for Save & Close
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [hasActualScoringData, setHasActualScoringData] = useState(false); // Track if real data exists in DB
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Ref to prevent duplicate loads
  const isLoadingRef = useRef(false);
  const loadedRiskIdRef = useRef<string>('');

  // Initialize calculator
  const calculator = useMemo(() => new RiskScoreCalculator(), []);

  // Load existing scoring data function
  const loadExistingScoringData = useCallback(async () => {
    // Prevent duplicate loads for same risk
    if (isLoadingRef.current && loadedRiskIdRef.current === riskId) {
      console.log('⏸️  [RiskScoringModal] Skipping duplicate load - already loading this risk');
      return;
    }
    console.log('📥 [RiskScoringModal] loadExistingScoringData() started');
    console.log('   ├─ Project ID:', projectId);
    console.log('   ├─ Risk ID:', riskId);
    console.log('   └─ Control Effectiveness:', controlEffectiveness);

    // Mark as loading
    isLoadingRef.current = true;
    loadedRiskIdRef.current = riskId;
    setIsLoading(true);
    try {
      console.log('🌐 [RiskScoringModal] Fetching existing scoring data from API...');
      const data = await riskScoringAPI.getRiskScoring(projectId, riskId);

      if (data) {
        console.log('✅ [RiskScoringModal] Existing scoring data found!');
        console.log('   📊 Data received:', JSON.stringify(data, null, 2));

        // Populate form with existing data, handle null values
        setLikelihoodData({
          ...data.likelihood,
          volumePeriod: data.likelihood.volumePeriod || 'monthly',
          automationLevel: data.likelihood.automationLevel || 'semi-automated',
          historicalIncidents: {
            count: data.likelihood.historicalIncidents?.count || 0,
            timeframe: data.likelihood.historicalIncidents?.timeframe || '12 months'
          }
        });
        console.log('   ✓ Likelihood data set:', data.likelihood);

        // Set default values for null impact fields
        setImpactData({
          financial: data.impact.financial || 3,
          reputational: data.impact.reputational || 3,
          regulatoryCompliance: data.impact.regulatoryCompliance || 3,
          operational: data.impact.operational || 3,
          strategic: data.impact.strategic || 3
        });
        console.log('   ✓ Impact data set (with defaults for nulls):', data.impact);

        setCalculatedScores({
          likelihood: data.calculatedScores.likelihood,
          impact: data.calculatedScores.impact,
          confidence: data.calculatedScores.confidence,
          reasoning: data.calculatedScores.reasoning,
          impactReasoning: data.calculatedScores.impactReasoning || '',
          finalRiskScore: data.calculatedScores.finalRiskScore
        });
        console.log('   ✓ Calculated scores set:', data.calculatedScores);

        // Check if we have actual saved scoring data (not just NULLs)
        const hasActualData = data.calculatedScores.likelihood !== null && data.calculatedScores.impact !== null;
        setHasActualScoringData(hasActualData);
        console.log('   ✓ Has actual scoring data:', hasActualData);

        setLastSaved(data.metadata.scoringLastUpdated ? new Date(data.metadata.scoringLastUpdated) : null);
        console.log('   ✓ Last saved timestamp:', data.metadata.scoringLastUpdated);
        console.log('✅ [RiskScoringModal] All existing data loaded successfully!');
      } else {
        console.log('📝 [RiskScoringModal] No existing scoring data found');
        console.log('   ℹ️  Using default values with control effectiveness:', controlEffectiveness || 3);

        // No existing data, use defaults with current values
        setLikelihoodData(prev => ({
          ...prev,
          controlEffectiveness: controlEffectiveness || 3
        }));
        console.log('   ✓ Default likelihood data set');
      }
    } catch (error) {
      console.error('❌ [RiskScoringModal] Error loading scoring data:', error);
      console.error('   Error details:', error);
      setErrorMessage('Failed to load existing scoring data');
    } finally {
      // Clear loading flag
      isLoadingRef.current = false;
      setIsLoading(false);
      console.log('🏁 [RiskScoringModal] loadExistingScoringData() completed');
    }
  }, [projectId, riskId, controlEffectiveness]);

  // Load existing scoring data when modal opens
  useEffect(() => {
    console.log('🎯 [RiskScoringModal] useEffect triggered - isOpen:', isOpen, 'riskId:', riskId);
    if (isOpen && riskId) {
      console.log('🔄 [RiskScoringModal] Calling loadExistingScoringData...');
      loadExistingScoringData();
    }
  }, [isOpen, riskId, loadExistingScoringData]);

  // Real-time calculation when data changes
  useEffect(() => {
    if (isLoading) {
      console.log('⏸️  [RiskScoringModal] Skipping calculation - still loading data');
      return; // Don't calculate during initial load
    }

    console.log('🧮 [RiskScoringModal] Real-time calculation triggered');
    console.log('   📥 Input - Likelihood Data:', likelihoodData);
    console.log('   📥 Input - Impact Data:', impactData);

    try {
      console.log('   🔢 Calculating likelihood score...');
      const likelihoodResult = calculator.calculateLikelihoodScore(likelihoodData);
      console.log('   ✅ Likelihood Result:', likelihoodResult);

      console.log('   🔢 Calculating impact score...');
      const impactResult = calculator.calculateImpactScore(impactData);
      console.log('   ✅ Impact Result:', impactResult);

      console.log('   🔢 Calculating final risk level...');
      const finalRisk = calculator.getFinalRiskLevel(likelihoodResult.score, impactResult.score);
      console.log('   ✅ Final Risk:', finalRisk);

      const newScores = {
        likelihood: likelihoodResult.score,
        impact: impactResult.score,
        confidence: likelihoodResult.confidence,
        reasoning: likelihoodResult.reasoning,
        impactReasoning: impactResult.reasoning,
        finalRiskScore: finalRisk.score
      };

      setCalculatedScores(newScores);
      console.log('✅ [RiskScoringModal] Calculated scores updated:', newScores);
    } catch (error) {
      console.error('❌ [RiskScoringModal] Error calculating scores:', error);
      console.error('   Error stack:', error);
    }
  }, [likelihoodData, impactData, calculator, isLoading]);

  // Save function
  const handleSave = useCallback(async (silent = false) => {
    console.log('💾 [RiskScoringModal] handleSave() called - silent:', silent);

    if (isSaving) {
      console.log('⏸️  [RiskScoringModal] Save already in progress, skipping');
      return;
    }

    console.log('🔄 [RiskScoringModal] Starting save process...');
    setIsSaving(true);
    setSaveStatus('saving');
    setErrorMessage('');

    try {
      const requestData: SaveRiskScoringRequest = {
        likelihood: likelihoodData,
        impact: impactData,
        calculatedScores: {
          likelihood: calculatedScores.likelihood,
          impact: calculatedScores.impact,
          confidence: calculatedScores.confidence,
          reasoning: calculatedScores.reasoning,
          impactReasoning: calculatedScores.impactReasoning
        }
      };

      console.log('📤 [RiskScoringModal] Sending save request to API...');
      console.log('   ├─ Project ID:', projectId);
      console.log('   ├─ Risk ID:', riskId);
      console.log('   └─ Request Data:', JSON.stringify(requestData, null, 2));

      await riskScoringAPI.saveRiskScoring(projectId, riskId, requestData);

      const savedTime = new Date();
      setLastSaved(savedTime);
      setSaveStatus('saved');
      setHasActualScoringData(true); // Mark that we now have saved data

      console.log('✅ [RiskScoringModal] Save successful!');
      console.log('   ├─ Saved at:', savedTime.toLocaleString());
      console.log('   ├─ Has actual data now: true');
      console.log('   └─ Silent save:', silent);

      if (!silent) {
        console.log('🔔 [RiskScoringModal] Manual save completed - user notified');
      }

      // Reset status after 3 seconds
      setTimeout(() => {
        if (saveStatus === 'saved') {
          setSaveStatus('idle');
          console.log('🔄 [RiskScoringModal] Save status reset to idle');
        }
      }, 3000);

    } catch (error: any) {
      console.error('❌ [RiskScoringModal] Save failed!');
      console.error('   ├─ Error message:', error.message);
      console.error('   ├─ Error object:', error);
      console.error('   └─ Error response:', error.response?.data);
      setSaveStatus('error');
      setErrorMessage(error.message || 'Failed to save risk scoring');
    } finally {
      setIsSaving(false);
      console.log('🏁 [RiskScoringModal] Save process completed');
    }
  }, [projectId, riskId, likelihoodData, impactData, calculatedScores, isSaving, saveStatus]);

  // Save and close
  const handleSaveAndClose = async () => {
    console.log('💾 [RiskScoringModal] handleSaveAndClose() called');

    if (isSavingAndClosing) {
      console.log('⏸️  [RiskScoringModal] Save and close already in progress, skipping');
      return;
    }

    console.log('🔄 [RiskScoringModal] Starting save and close process...');
    setIsSavingAndClosing(true);
    setSaveStatus('saving');
    setErrorMessage('');

    try {
      const requestData: SaveRiskScoringRequest = {
        likelihood: likelihoodData,
        impact: impactData,
        calculatedScores: {
          likelihood: calculatedScores.likelihood,
          impact: calculatedScores.impact,
          confidence: calculatedScores.confidence,
          reasoning: calculatedScores.reasoning,
          impactReasoning: calculatedScores.impactReasoning
        }
      };

      console.log('📤 [RiskScoringModal] Sending save and close request to API...');
      await riskScoringAPI.saveRiskScoring(projectId, riskId, requestData);

      const savedTime = new Date();
      setLastSaved(savedTime);
      setSaveStatus('saved');
      setHasActualScoringData(true);

      console.log('✅ [RiskScoringModal] Save and close successful!');

      if (onSaveComplete) {
        onSaveComplete(); // Refresh parent data
      }
      onClose();

    } catch (error: any) {
      console.error('❌ [RiskScoringModal] Save and close failed!');
      console.error('   ├─ Error message:', error.message);
      setSaveStatus('error');
      setErrorMessage(error.message || 'Failed to save risk scoring');
    } finally {
      setIsSavingAndClosing(false);
      console.log('🏁 [RiskScoringModal] Save and close process completed');
    }
  };

  // Get risk level styling
  const getRiskLevelStyle = (score: number) => {
    if (score >= 20) return 'bg-red-100 text-red-800 border-red-300';
    if (score >= 15) return 'bg-orange-100 text-orange-800 border-orange-300';
    if (score >= 8) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-green-100 text-green-800 border-green-300';
  };

  const getRiskLevelLabel = (score: number) => {
    if (score >= 20) return 'CRITICAL';
    if (score >= 15) return 'HIGH';
    if (score >= 8) return 'MEDIUM';
    return 'LOW';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Risk Assessment: Likelihood & Impact</h2>
              <p className="text-blue-100 text-sm mt-1">
                AI-assisted risk rating for: {processStepName}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Save Status Indicator */}
              {lastSaved && (
                <div className="text-sm text-blue-100 flex items-center gap-2">
                  {saveStatus === 'saved' && <CheckCircle className="w-4 h-4" />}
                  {saveStatus === 'saving' && <RefreshCw className="w-4 h-4 animate-spin" />}
                  {saveStatus === 'error' && <AlertCircle className="w-4 h-4" />}
                  <span>
                    {saveStatus === 'saving' && 'Saving...'}
                    {saveStatus === 'saved' && 'Saved'}
                    {saveStatus === 'error' && 'Save failed'}
                    {saveStatus === 'idle' && `Last saved: ${lastSaved.toLocaleTimeString()}`}
                  </span>
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-50 border-l-4 border-red-500 px-6 py-3 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-800">{errorMessage}</p>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-3 text-gray-600">Loading scoring data...</span>
              </div>
            ) : (
              <>
                {/* Likelihood Assessment Section */}
                <LikelihoodAssessment
                  data={likelihoodData}
                  onChange={setLikelihoodData}
                  calculatedScore={calculatedScores.likelihood}
                  confidence={calculatedScores.confidence}
                  reasoning={calculatedScores.reasoning}
                />

                {/* Divider */}
                <div className="border-t border-gray-200 my-6" />

                {/* Impact Assessment Section */}
                <ImpactAssessment
                  data={impactData}
                  onChange={setImpactData}
                  calculatedScore={calculatedScores.impact}
                  reasoning={calculatedScores.impactReasoning}
                />

                {/* Final Risk Score Display - Only show if data has been saved */}
                {hasActualScoringData ? (
                  <div className={`mt-6 p-6 rounded-lg border-2 ${getRiskLevelStyle(calculatedScores.finalRiskScore)}`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Final Risk Score</h3>
                      <span className="px-4 py-2 rounded-full text-sm font-bold">
                        {getRiskLevelLabel(calculatedScores.finalRiskScore)} RISK
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Likelihood</div>
                        <div className="text-3xl font-bold">{calculatedScores.likelihood}/5</div>
                      </div>
                      <div className="text-center flex items-center justify-center">
                        <span className="text-2xl">×</span>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Impact</div>
                        <div className="text-3xl font-bold">{calculatedScores.impact}/5</div>
                      </div>
                    </div>

                    <div className="text-center py-4 border-t border-current border-opacity-20">
                      <div className="text-sm text-gray-600 mb-1">Final Risk Score</div>
                      <div className="text-5xl font-bold">{calculatedScores.finalRiskScore}</div>
                    </div>

                    {/* Comparison with original AI scores */}
                    {(currentLikelihood || currentImpact) && (
                      <div className="mt-4 pt-4 border-t border-current border-opacity-20">
                        <div className="text-sm">
                          <strong>Original AI Assessment:</strong> Likelihood {currentLikelihood}/5 × Impact {currentImpact}/5 = {(currentLikelihood || 0) * (currentImpact || 0)}
                        </div>
                        {calculatedScores.finalRiskScore !== (currentLikelihood || 0) * (currentImpact || 0) && (
                          <div className="text-sm mt-1">
                            <strong>Change:</strong> {calculatedScores.finalRiskScore > (currentLikelihood || 0) * (currentImpact || 0) ? 'Increased' : 'Decreased'} from original assessment
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-6 p-6 rounded-lg border-2 border-gray-300 bg-gray-50">
                    <div className="text-center">
                      <div className="text-lg font-semibold text-gray-900 mb-2">Risk Assessment Not Yet Completed</div>
                      <p className="text-sm text-gray-600 mb-4">
                        Fill in the likelihood and impact factors above, then click "Save Assessment" to calculate the final risk score.
                      </p>
                      {(currentLikelihood || currentImpact) && (
                        <div className="mt-4 pt-4 border-t border-gray-300">
                          <div className="text-sm text-gray-700">
                            <strong>Current AI Initial Assessment:</strong> Likelihood {currentLikelihood}/5 × Impact {currentImpact}/5 = {(currentLikelihood || 0) * (currentImpact || 0)}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Complete the structured assessment above for a more accurate risk score
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-600 flex items-center gap-2">
              <Info className="w-4 h-4" />
              <span>Changes are auto-saved every 10 seconds</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleSave(false)}
                disabled={isSaving}
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  if (onSaveComplete) {
                    onSaveComplete(); // Refresh parent data before closing
                  }
                  onClose();
                }}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
